import { Title } from '@cognite/cogs.js';
import React from 'react';
import styled from 'styled-components/macro';

const StepNumber = ({
  step,
  hideLine,
}: {
  step: number;
  hideLine?: boolean;
}) => (
  <StyledNumberItem>
    <div className="stepNumber">{step}</div>
    {!hideLine && <div className="stepLine" />}
  </StyledNumberItem>
);
StepNumber.defaultProps = {
  hideLine: false,
};

export const WorkflowTutorial = () => (
  <StyledContainer>
    <TitleStyled>How does it work?</TitleStyled>
    <StyledStep>
      <StyledSideItem>
        <StyleStepTitle level={3}>
          Create properties using GraphQL
        </StyleStepTitle>
        <p>
          Set up your schema and define your properties in GraphQL. The
          properties define what data your app needs, and make it possible to
          fetch the data from a data source.
        </p>
      </StyledSideItem>
      <StepNumber step={1} />
      <StyledSideImage>
        <StyledImageContainer>
          <StyledImage
            src={`${process.env.PUBLIC_URL}/images/workflow/workflow-step-1.svg`}
            alt="Create a project"
            width="80"
            height="80"
          />
        </StyledImageContainer>
      </StyledSideImage>
    </StyledStep>
    <StyledStep>
      <StyledSideImage>
        <StyledImageContainer>
          <StyledImage
            src={`${process.env.PUBLIC_URL}/images/workflow/workflow-step-data-ingestion.svg`}
            alt="Create a project"
            width="80"
            height="80"
            style={{ position: 'relative', left: '2px' }}
          />
        </StyledImageContainer>
      </StyledSideImage>
      <StepNumber step={2} />
      <StyledSideItem>
        <StyleStepTitle level={3}>Create mutations</StyleStepTitle>
        <p>
          After the creation of the schema, you can play with queries and
          mutations in Query Explorer. It can help you build mutations that can
          be used in the ingestion of data to your graph data model.
        </p>
      </StyledSideItem>
    </StyledStep>
    <StyledStep>
      <StyledSideItem>
        <StyleStepTitle level={3}>Ingest the data</StyleStepTitle>
        <p>
          Since ITG is in its early development stage and the tool for data
          ingestion is not completely functional yet, please use your code with
          needed mutations for publishing data to your schema.
        </p>
      </StyledSideItem>
      <StepNumber step={3} />
      <StyledSideImage>
        <StyledImageContainer>
          <StyledImage
            src={`${process.env.PUBLIC_URL}/images/workflow/workflow-step-2.svg`}
            alt="Create a project"
            width="85"
            height="85"
            style={{ position: 'relative', left: '5px' }}
          />
        </StyledImageContainer>
      </StyledSideImage>
    </StyledStep>
    <StyledStep>
      <StyledSideImage>
        <StyledImageContainer>
          <StyledImage
            src={`${process.env.PUBLIC_URL}/images/workflow/workflow-step-4.svg`}
            alt="Create a project"
            width="105"
            height="105"
            style={{ position: 'relative', left: '5px' }}
          />
        </StyledImageContainer>
      </StyledSideImage>
      <StepNumber step={4} hideLine />
      <StyledSideItem>
        <StyleStepTitle level={3}>Integrate with your app</StyleStepTitle>
        <p>
          When the data is fetched, you can use the created GraphQL endpoint to
          integrate the fetched data into your application, your Power BI
          dashboard or other tools you use.
        </p>
      </StyledSideItem>
    </StyledStep>
  </StyledContainer>
);

// Styles
const StyledContainer = styled.div`
  width: 700px;
  margin: 0 auto;
`;

const TitleStyled = styled(Title)`
  font-size: 1.4rem;
  margin-bottom: 3rem;
  text-align: center;
`;

const StyleStepTitle = styled(Title)`
  font-size: 1.1rem;
  font-weight: 500;
  margin: 1rem 1rem 1rem 0;
`;

const StyledStep = styled.div`
  display: flex;
`;

const StyledSideItem = styled.div`
  flex: 1;
`;

const StyledSideImage = styled.div`
  display: flex;
  flex: 1;
  text-align: center;
  justify-content: center;
  align-content: center;
`;

const StyledNumberItem = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0;
  padding: 0 2rem 1rem 2rem;
  min-height: 200px;

  .stepNumber {
    display: inline-flex;
    border: solid 2px #4a67fb;
    color: #4a67fb;
    font-weight: 500;
    font-size: 1.3rem;
    border-radius: 50%;
    width: 2.8rem;
    height: 2.8rem;
    padding-top: 0.3rem;
    justify-content: center;
  }

  .stepLine {
    flex: 1;
    border-left: solid 1px #000;
    position: relative;
    left: 1.5rem;
    margin-top: 1rem;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  height: 150px;
  width: 150px;
  background-color: #fff;
  padding: 1rem;
  border-radius: 50%;
  justify-content: center;
  align-self: center;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
`;

const StyledImage = styled.img`
  align-self: center;
`;
